import { useSession } from '@faststore/sdk'
import type { FC } from 'react'
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  DeliveryEstimateDate,
  DeliveryEstimateRange,
  FavouriteStore,
  FavouriteStorePickingTime,
  SkuAvailability,
} from 'src/components/sections/Store/typings'
import { getSessionFavouriteStore } from 'src/components/sections/Store/utilities'
import { useStoreAPI } from 'src/sdk/store/useStoreAPI'
import { useModal } from 'src/sdk/ui/modal/useModal'
import capitalizeFirstLetter from 'src/utils/stringUtils'

export type StoreContext = {
  favouriteStore: FavouriteStore | null
  setFavouriteStore: (a: FavouriteStore) => void
  openStoreModal: boolean
  handleOpenStoreModal: (a: boolean) => void
  selectedSku: string
  setSelectedSku: (a: string) => void
  productsAvailableInStore: Array<string>
  setProductsAvailableInStore: (a: Array<string>) => void
  deliveryEstimate: DeliveryEstimateDate | DeliveryEstimateRange | null
  setDeliveryEstimate: (
    a: DeliveryEstimateDate | DeliveryEstimateRange | null
  ) => void
  showSavedStoreToast: boolean
  setShowSavedStoreToast: (a: boolean) => void
  showUpdatedStoreToast: boolean
  setShowUpdatedStoreToast: (a: boolean) => void
  pickingTime: FavouriteStorePickingTime | null
  setPickingTime: (a: FavouriteStorePickingTime | null) => void
  showEmailInfoToast: boolean
  setShowEmailInfoToast: (a: boolean) => void
  showStoreData: boolean
  setShowStoreData: (a: boolean) => void
  products: Array<SkuAvailability>
  setProducts: (a: Array<SkuAvailability>) => void
  availableProduct: string
  setAvailableProduct: (a: string) => void
  openStoreInfo: boolean
  handleOpenStoreInfo: (a: boolean) => void
}

const StoreContext = createContext({} as StoreContext)
export const StoreProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { person } = useSession()

  const [favouriteStore, setFavouriteStore] = useState<FavouriteStore | null>(
    null
  )
  const [pickingTime, setPickingTime] =
    useState<FavouriteStorePickingTime | null>(null)
  const [deliveryEstimate, setDeliveryEstimate] = useState<
    DeliveryEstimateDate | DeliveryEstimateRange | null
  >(null)

  const [showSavedStoreToast, setShowSavedStoreToast] = useState<boolean>(false)
  const [showUpdatedStoreToast, setShowUpdatedStoreToast] =
    useState<boolean>(false)
  const [showEmailInfoToast, setShowEmailInfoToast] = useState<boolean>(false)
  const { openStoreModal, handleOpenStoreModal } = useModal()
  const [selectedSku, setSelectedSku] = useState<string>('')
  const [productsAvailableInStore, setProductsAvailableInStore] = useState<
    Array<string>
  >([])
  const [showStoreData, setShowStoreData] = useState<boolean>(false)
  const [products, setProducts] = useState<Array<SkuAvailability>>([])
  const [availableProduct, setAvailableProduct] = useState<string>('')
  const { openStoreInfo, handleOpenStoreInfo } = useModal()
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const {
    getFavouriteStore,
    savePickupPointDataInSession,
    setFavouriteStoreInSession,
    getPickupPointById,
  } = useStoreAPI()

  useEffect(() => {
    setShowStoreData(false)

    const favouriteStore = getSessionFavouriteStore()
    // this func saves favourite store in browser session
    const saveFavouriteStoreInSession = async (store: any) => {
      try {
        const pickupPoint = await getPickupPointById(`AGRI${store.Id}`)
        if (pickupPoint) {
          const favouriteStore = {
            address: {
              street: store.Street,
              // street: pickupPoint?.address?.street,
              city: pickupPoint?.address?.city,
              state: pickupPoint?.address?.state,
              number: pickupPoint?.address?.number,
            },
            id: store?.Id,
            name: `Arcaplanet - ${capitalizeFirstLetter(
              store?.City?.toLowerCase()
            )} ${capitalizeFirstLetter(store?.Street?.toLowerCase())}`,
            postalCode: store?.PostalCode,
            sellerId: store?.SellerId,
            geoCoordinates: [
              pickupPoint?.address?.location?.longitude,
              pickupPoint?.address?.location?.latitude,
            ],
            vtexSellerId: pickupPoint?.id,
          }

          setFavouriteStore(favouriteStore)
          savePickupPointDataInSession(
            favouriteStore.id,
            favouriteStore.vtexSellerId ?? favouriteStore.sellerId
          )

          setFavouriteStoreInSession(favouriteStore)
        }
      } catch (err) {
        console.log(err)
      } finally {
        setShowStoreData(true)
      }
    }

    // if user is guest and already has store saved in browser session:
    if (favouriteStore) {
      setFavouriteStore(favouriteStore)
      savePickupPointDataInSession(
        favouriteStore.id,
        favouriteStore.vtexSellerId ?? favouriteStore.sellerId
      )
      // We add the case when user is initially guest, selects a store (that goes in session), then logs in and the store from SF is different than the one in session: we show a toast that says that the store has been updated
      if (person?.email) {
        getFavouriteStore(person?.email).then((store: any) => {
          if (store) {
            if (store.Id !== favouriteStore.id) {
              saveFavouriteStoreInSession(store)

              setShowUpdatedStoreToast(true)
              timerRef.current = setTimeout(() => {
                setShowUpdatedStoreToast(false)
              }, 5000)
            }
          }
        })
      }

      setShowStoreData(true)
    } else {
      // If user is instead logged in, we get the favourite store from SF and save it in session
      person?.email &&
        getFavouriteStore(person?.email).then(async (store: any) => {
          if (store) {
            saveFavouriteStoreInSession(store)
          }
        })
    }

    setShowStoreData(true)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [person?.email])

  const value = {
    favouriteStore,
    setFavouriteStore,
    openStoreModal,
    handleOpenStoreModal,
    selectedSku,
    setSelectedSku,
    productsAvailableInStore,
    setProductsAvailableInStore,
    deliveryEstimate,
    setDeliveryEstimate,
    showSavedStoreToast,
    setShowSavedStoreToast,
    showUpdatedStoreToast,
    setShowUpdatedStoreToast,
    pickingTime,
    setPickingTime,
    showEmailInfoToast,
    setShowEmailInfoToast,
    setShowStoreData,
    showStoreData,
    products,
    setProducts,
    availableProduct,
    setAvailableProduct,
    openStoreInfo,
    handleOpenStoreInfo,
  }

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
}

export const useStoreContext = () => {
  return useContext(StoreContext)
}
